/*
 * "השלימו את הסט" — bundle picker on the product page.
 *
 * Rows come from the product's custom.complete_the_set metafield, rendered
 * server-side into a JSON blob. The shopper ticks pieces, picks a variant per
 * piece, optionally swaps a piece for another of the same type (from
 * custom.set_alternatives), and adds the lot in one request.
 *
 * Cart plumbing follows assets/frequently-bought-together.js exactly so the
 * drawer, the count bubble and the notification behave as everywhere else.
 */
if (!window.Eurus.loadedScript.has('complete-the-set.js')) {
  window.Eurus.loadedScript.add('complete-the-set.js');

  requestAnimationFrame(() => {
    document.addEventListener('alpine:init', () => {
      Alpine.data('xCompleteSet', (rawItems, moneyFormat, currentVariantId) => ({
        items: [],
        loading: false,
        errorMessage: '',
        swapFor: null,        // index of the row whose swap picker is open

        init() {
          this.items = (rawItems || []).map((item) => {
            // The locked row must follow the picker at the top of the page, not
            // guess: otherwise a shopper who chose 18cm in gold would get the
            // first available variant added instead.
            let first = null;
            if (item.isCurrent && currentVariantId) {
              first = item.variants.find((v) => v.id === Number(currentVariantId)) || null;
            }
            if (!first) first = item.variants.find((v) => v.available) || item.variants[0] || null;
            return {
              ...item,
              selected: item.available !== false,
              variantId: first ? first.id : null,
              // the piece currently shown in this row; swapping replaces it
              current: {
                id: item.id, title: item.title, type: item.type, url: item.url,
                image: item.image, available: item.available, variants: item.variants,
              },
            };
          });

          this.matchFinishes();

          // keep the locked row in step with the main variant picker
          document.addEventListener('eurus:variant:change', (e) => {
            const v = e.detail && e.detail.variant;
            if (!v || !v.id) return;
            const row = this.items.find((r) => r.isCurrent);
            if (!row) return;
            // ignore events from other sections (quick view, siblings)
            if (!row.current.variants.some((x) => x.id === Number(v.id))) return;
            row.variantId = Number(v.id);
            this.matchFinishes();
          });
        },

        // Which metal a variant is, whatever wording the supplier used. Plating
        // is what the eye sees, so rose beats gold beats the base metal:
        // "כסף 925 בציפוי רוז גולד 14K" is a rose piece, not a silver one.
        finishOf(title) {
          const t = String(title || '');
          if (/רוז|rose/i.test(t)) return 'rose';
          if (/זהב|gold/i.test(t)) return 'gold';
          if (/כסף|silver|כרום/i.test(t)) return 'silver';
          if (/שחור|black/i.test(t)) return 'black';
          return null;
        },

        // Pick the chosen piece in gold and the pieces beside it follow, when
        // they come in gold at all. A row that cannot match is left as it was
        // rather than switched to something arbitrary.
        matchFinishes() {
          const lead = this.items.find((r) => r.isCurrent);
          if (!lead) return;
          const want = this.finishOf((this.variantOf(lead) || {}).title);
          if (!want) return;
          this.items.forEach((row) => {
            if (row.isCurrent) return;
            const current = this.variantOf(row);
            if (current && current.available && this.finishOf(current.title) === want) return;
            const match = row.current.variants.find(
              (v) => v.available && this.finishOf(v.title) === want
            );
            if (match) row.variantId = match.id;
          });
        },

        variantOf(row) {
          return row.current.variants.find((v) => v.id === Number(row.variantId)) || row.current.variants[0] || null;
        },

        rowPrice(row) {
          const v = this.variantOf(row);
          return v ? v.price : 0;
        },

        rowPriceText(row) {
          return this.money(this.rowPrice(row));
        },

        get chosen() {
          return this.items.filter((r) => r.selected && this.variantOf(r) && this.variantOf(r).available);
        },

        get total() {
          return this.chosen.reduce((sum, r) => sum + this.rowPrice(r), 0);
        },

        get totalText() {
          return this.money(this.total);
        },

        get canAdd() {
          return this.chosen.length > 0 && !this.loading;
        },

        money(cents) {
          const helper = Alpine.store('xHelper');
          if (helper && typeof helper.formatMoney === 'function') {
            return helper.formatMoney(cents, moneyFormat);
          }
          return (cents / 100).toFixed(2);
        },

        /* ---------- swapping a piece ---------- */

        openSwap(index) {
          this.swapFor = this.swapFor === index ? null : index;
        },

        closeSwap() {
          this.swapFor = null;
        },

        applySwap(index, alt) {
          const row = this.items[index];
          const first = alt.variants.find((v) => v.available) || alt.variants[0] || null;
          row.current = {
            id: alt.id, title: alt.title, type: alt.type, url: alt.url, image: alt.image,
            available: alt.variants.some((v) => v.available), variants: alt.variants,
          };
          row.variantId = first ? first.id : null;
          row.selected = true;
          this.swapFor = null;
        },

        /* ---------- add the bundle ---------- */

        async addSet(el) {
          if (!this.canAdd) return;
          this.loading = true;
          this.errorMessage = '';

          await Alpine.store('xCartHelper').waitForCartUpdate();
          window.updatingCart = true;

          const items = this.chosen.map((r) => ({ id: Number(r.variantId), quantity: 1 }));
          const sections = Alpine.store('xCartHelper').getSectionsToRender().map((s) => s.id);

          try {
            const res = await fetch(window.Shopify.routes.root + 'cart/add.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ items: items, sections: sections }),
            });
            const data = await res.json();

            if (data.status == '422' || data.status === 422) {
              this.errorMessage = data.description || data.message || '';
              document.dispatchEvent(new CustomEvent('eurus:product:add-failed', {
                detail: { errorMessage: this.errorMessage },
              }));
              return;
            }

            Alpine.store('xCartHelper').reRenderSections(data.sections);
            document.dispatchEvent(new CustomEvent('eurus:product:added', { detail: { product: data.items } }));

            if (Alpine.store('xQuickView') && Alpine.store('xQuickView').show) {
              Alpine.store('xQuickView').show = false;
            }
            if (Alpine.store('xCartNoti') && Alpine.store('xCartNoti').enable) {
              Alpine.store('xCartNoti').setItem(data);
            } else {
              Alpine.store('xMiniCart').openCart();
              document.dispatchEvent(new CustomEvent('eurus:cart:redirect'));
            }

            // The theme's own copy of this line does parseInt(bubble.innerHTML),
            // which is NaN because the bubble wraps the number in a div. Read
            // the value properly and only assign when it really is a number.
            const bubble = document.getElementById('cart-icon-bubble');
            if (bubble) {
              const holder = bubble.querySelector('[data-value]');
              const raw = holder && holder.dataset.value !== undefined
                ? holder.dataset.value
                : bubble.textContent;
              const count = parseInt(String(raw).trim(), 10);
              if (!Number.isNaN(count)) Alpine.store('xCartHelper').currentItemCount = count;
            }
            document.dispatchEvent(new CustomEvent('eurus:cart:items-changed'));
          } catch (e) {
            this.errorMessage = (el && el.dataset.genericError) || '';
          } finally {
            window.updatingCart = false;
            this.loading = false;
          }
        },
      }));
    });
  });
}
